/**
 * Cross-platform file download / share for transcript / recording downloads.
 *
 * - Web: caller's existing `<a href download>` does the right thing —
 *   the browser handles the download natively with the SESSIONID cookie
 *   for auth. This helper is a no-op there and {@link shouldInterceptDownload}
 *   returns false so callers can skip `preventDefault`.
 * - Capacitor (iOS/Android WebView): `<a href download>` opens an
 *   external Safari/Chrome window (iOS) or silently fails (some Android
 *   configurations). Instead, fetch the file with the user's JWT,
 *   construct a `File` object, and hand it to the Web Share API. iOS
 *   WKWebView 17+ surfaces the native share sheet (Save to Files,
 *   AirDrop, Mail, ...). If the share API isn't available we fall back
 *   to an in-WebView blob-URL anchor click so the file at least stays
 *   in the app process; the user can long-press to save via the system
 *   action sheet.
 */
import { Capacitor } from "@capacitor/core";
import { getAuthToken } from "./providers";

const isNative = Capacitor.isNativePlatform();

interface ShareOptions {
  /** Default filename the user is offered for save. */
  filename: string;
  /** Optional MIME hint when the server response lacks Content-Type. */
  mimeType?: string;
  /** Share-sheet title (used on iOS). */
  title?: string;
}

/**
 * True when this platform needs JS to take over a download (mobile);
 * false when a plain `<a download>` is sufficient (web). Callers gate
 * `preventDefault()` on this so the browser's native path still runs
 * on desktop without redundant DOM work.
 */
export function shouldInterceptDownload(): boolean {
  return isNative;
}

/**
 * Mobile-only: fetch the file with auth, then surface a native share
 * sheet (Save to Files, AirDrop, ...). On web this is a no-op — callers
 * should let their `<a download>` fire instead (see
 * {@link shouldInterceptDownload}).
 */
export async function shareFileFromUrl(url: string, opts: ShareOptions): Promise<void> {
  if (!isNative) return;
  const blob = await fetchAuthedBlob(url, opts.mimeType);
  await shareOrSaveBlob(blob, opts);
}

/**
 * Cross-platform download: fetch the file with auth and hand it to the
 * user — on mobile via the native share sheet (Save to Files / AirDrop),
 * on web/desktop via a blob-anchor click. Unlike {@link shareFileFromUrl}
 * (mobile-only) this always does the work, so callers don't need a
 * separate `<a download>` for the web path.
 *
 * A blob-anchor on a `blob:` URL always honors the `download` attribute,
 * even for files served from a cross-origin host — which a plain remote
 * `<a download>` would instead open in a new tab.
 *
 * Pass a `blob:` URL (already-fetched media) to skip the auth token
 * round-trip and avoid re-downloading the file.
 */
export async function downloadFile(url: string, opts: ShareOptions): Promise<void> {
  const blob = await fetchAuthedBlob(url, opts.mimeType);
  await shareOrSaveBlob(blob, opts);
}

/**
 * Fetch `url` into a Blob, attaching the user's JWT for cross-origin
 * media. `blob:` URLs are already in-memory, so we skip the token
 * round-trip and the network fetch is local/instant.
 */
async function fetchAuthedBlob(url: string, mimeType?: string): Promise<Blob> {
  const isBlobUrl = url.startsWith("blob:");
  const token = isBlobUrl ? "" : await getAuthToken();
  const resp = await fetch(url, {
    headers: token ? { Authorization: token } : {},
    credentials: "include",
  });
  if (!resp.ok) throw new Error(`Download failed: HTTP ${resp.status}`);
  const blob = await resp.blob();
  if (mimeType && !blob.type) return new Blob([blob], { type: mimeType });
  return blob;
}

/**
 * Hand a fetched Blob to the user: native share sheet on mobile (with a
 * blob-anchor fallback for Web Share-unavailable WebViews), blob-anchor
 * click on web/desktop.
 */
async function shareOrSaveBlob(blob: Blob, opts: ShareOptions): Promise<void> {
  const type = blob.type || opts.mimeType || "application/octet-stream";

  if (isNative) {
    const file = new File([blob], opts.filename, { type });
    const shareNav = navigator as Navigator & {
      canShare?: (data: { files?: File[] }) => boolean;
      share?: (data: { files?: File[]; title?: string }) => Promise<void>;
    };
    if (shareNav.canShare?.({ files: [file] }) && shareNav.share) {
      try {
        await shareNav.share({ files: [file], title: opts.title || opts.filename });
        return;
      } catch (e) {
        // User-cancelled share counts as handled — don't fall back to a
        // blob-anchor that would force the user through another dismissal.
        const name = (e as Error)?.name;
        if (name === "AbortError" || name === "NotAllowedError") return;
        // Anything else — fall through to the blob-anchor fallback below.
      }
    }
  }

  // Web/desktop, and the fallback for Web Share-unavailable WebViews
  // (pre-iOS 17, some Android configurations).
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = opts.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Release the object URL on the next tick — must outlive the click.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

/** Helper to derive a sensible filename from a URL path. */
export function filenameFromUrl(url: string, fallback: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const last = u.pathname.split("/").filter(Boolean).pop();
    if (last && /\.[a-zA-Z0-9]{1,8}$/.test(last)) return last;
  } catch { /* ignore */ }
  return fallback;
}
